import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';

import { AddAddressFormManual, AddAddressFormMap } from './AddAddressForm';
import Button from './Button';
import Modal from './Modal';
import CheckoutSectionHeading from './checkout/CheckoutSectionHeading';
import CheckoutSelectionCard from './checkout/CheckoutSelectionCard';
import { v2Colors } from '../design/theme';
import { addPilotUserAddress, getPilotUserAddresses } from '../services/addresses';
import { V2ApiError } from '../services/client';
import { readLocationCache } from '../services/locationCache';
import {
  deliveryUnavailableMessage,
  formatAddressLine,
  resolveAndMergeForAddress,
} from '../services/locationResolve';
import { useV2Common } from '../state/CommonProvider';
import { useV2Session } from '../state/SessionProvider';

const ADDRESS_PREVIEW_COUNT = 3;

export default function CheckoutAddresses({
  onAfterLocationMerge,
  heading = 'Delivery address',
  variant = 'default',
}) {
  const { mobile } = useV2Session();
  const { isMobile } = useV2Common();
  const isCheckout = variant === 'checkout';
  const mapKey = (process.env.REACT_APP_MAP_KEY || '').trim();

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selectError, setSelectError] = useState('');
  const [resolvingStore, setResolvingStore] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addStep, setAddStep] = useState('pin');
  const [showAllAddresses, setShowAllAddresses] = useState(false);

  const openAddModal = useCallback(() => {
    setAddStep('pin');
    setAddOpen(true);
  }, []);

  const closeAddModal = useCallback(() => {
    setAddOpen(false);
    setAddStep('pin');
  }, []);

  const onAddStepChange = useCallback((step) => {
    setAddStep(step);
  }, []);

  const loadList = useCallback(async () => {
    if (!mobile) return;
    setLoading(true);
    setListError('');
    try {
      const list = await getPilotUserAddresses(mobile);
      setAddresses(list);
      setShowAllAddresses(false);
    } catch (e) {
      setListError(e instanceof V2ApiError ? e.message : 'Could not load addresses.');
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  }, [mobile]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loc = await readLocationCache();
      const id = loc?.customerAddress?.addressId;
      if (!cancelled && id) setSelectedId(String(id));
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSelectAddress = useCallback(async (addr) => {
    if (!addr?.addressId) return;
    setSelectedId(String(addr.addressId));
    setSelectError('');
    const lat = Number(addr.latLong?.lat);
    const lng = Number(addr.latLong?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setSelectError('This address has no map coordinates. Add a new address with a map pin or coordinates.');
      return;
    }

    setResolvingStore(true);
    let resolvedStoreId = null;
    try {
      const { storeId } = await resolveAndMergeForAddress(addr);
      resolvedStoreId = storeId;
    } catch (e) {
      setSelectError(deliveryUnavailableMessage(e));
      resolvedStoreId = null;
    } finally {
      setResolvingStore(false);
      onAfterLocationMerge?.({ storeId: resolvedStoreId });
    }
  }, [onAfterLocationMerge]);

  const handleAddSuccess = useCallback(
    async (body) => {
      const address = await addPilotUserAddress(mobile, body);
      await loadList();
      closeAddModal();
      if (address) {
        await handleSelectAddress(address);
      }
      return address;
    },
    [mobile, loadList, handleSelectAddress, closeAddModal]
  );

  const orderedAddresses = useMemo(() => {
    if (!selectedId) return addresses;
    const sel = addresses.find((a) => String(a.addressId) === String(selectedId));
    if (!sel) return addresses;
    const rest = addresses.filter((a) => String(a.addressId) !== String(selectedId));
    return [sel, ...rest];
  }, [addresses, selectedId]);

  const addressListExpanded = useMemo(() => {
    if (orderedAddresses.length <= ADDRESS_PREVIEW_COUNT) return true;
    return showAllAddresses;
  }, [orderedAddresses.length, showAllAddresses]);

  const visibleAddresses = useMemo(() => {
    if (addressListExpanded) return orderedAddresses;
    return orderedAddresses.slice(0, ADDRESS_PREVIEW_COUNT);
  }, [orderedAddresses, addressListExpanded]);

  const showAddressShowMore =
    orderedAddresses.length > ADDRESS_PREVIEW_COUNT && !addressListExpanded;
  const showAddressShowLess =
    orderedAddresses.length > ADDRESS_PREVIEW_COUNT && showAllAddresses;

  if (!mobile) {
    return <Alert severity="info">Sign in to manage delivery addresses.</Alert>;
  }

  const headingEl = isCheckout ? (
    <CheckoutSectionHeading>{heading}</CheckoutSectionHeading>
  ) : (
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
      {heading}
    </Typography>
  );

  return (
    <Box>
      {headingEl}

      {listError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
        </Alert>
      ) : null}
      {selectError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {selectError}
        </Alert>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={32} />
        </Box>
      ) : addresses.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No saved addresses yet.
        </Typography>
      ) : (
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            opacity: resolvingStore ? 0.7 : 1,
            pointerEvents: resolvingStore ? 'none' : 'auto',
          }}
        >
          {visibleAddresses.map((addr) => {
            const id = String(addr.addressId);
            const selected = selectedId === id;
            return (
              <CheckoutSelectionCard
                key={id}
                selected={selected}
                onClick={() => handleSelectAddress(addr)}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 0.25, color: selected ? v2Colors.canvas : v2Colors.heading }}
                >
                  {addr.userName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.45,
                    color: selected ? v2Colors.canvas : v2Colors.textMuted,
                    opacity: selected ? 0.92 : 1,
                  }}
                >
                  {formatAddressLine(addr)}
                </Typography>
                {isCheckout && mobile ? (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      color: selected ? v2Colors.canvas : v2Colors.textMuted,
                      opacity: selected ? 0.92 : 1,
                    }}
                  >
                    {mobile}
                  </Typography>
                ) : null}
              </CheckoutSelectionCard>
            );
          })}
          {showAddressShowMore ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.25 }}>
              <Box
                component="button"
                type="button"
                onClick={() => setShowAllAddresses(true)}
                sx={{
                  border: 'none',
                  margin: 0,
                  cursor: 'pointer',
                  borderRadius: 2,
                  px: 2,
                  py: 0.75,
                  typography: 'body2',
                  fontWeight: 600,
                  color: v2Colors.primary,
                  fontFamily: 'inherit',
                  bgcolor: 'transparent',
                  textDecoration: 'underline',
                  '&:hover': { opacity: 0.85 },
                }}
              >
                Show all addresses
              </Box>
            </Box>
          ) : null}
          {showAddressShowLess ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                component="button"
                type="button"
                onClick={() => setShowAllAddresses(false)}
                sx={{
                  border: 'none',
                  margin: 0,
                  cursor: 'pointer',
                  borderRadius: 2,
                  px: 2,
                  py: 0.75,
                  typography: 'body2',
                  color: v2Colors.primaryMuted,
                  fontFamily: 'inherit',
                  bgcolor: 'transparent',
                  '&:hover': { opacity: 0.85 },
                }}
              >
                Show less
              </Box>
            </Box>
          ) : null}
        </Box>
      )}

      {resolvingStore ? (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Finding nearest store…
          </Typography>
        </Box>
      ) : null}

      {isCheckout ? (
        <Box
          component="button"
          type="button"
          onClick={openAddModal}
          sx={{
            width: '100%',
            py: 1.5,
            borderRadius: 2,
            border: `1px dashed ${v2Colors.primary}`,
            bgcolor: v2Colors.canvas,
            color: v2Colors.primary,
            fontWeight: 600,
            fontSize: '0.9rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
            '&:hover': { bgcolor: v2Colors.surface },
          }}
        >
          + Add new address
        </Box>
      ) : (
        <Button variant="outlined" fullWidth onClick={openAddModal} sx={{ borderRadius: '5px' }}>
          Add new address
        </Button>
      )}

      <Modal
        open={addOpen}
        onClose={closeAddModal}
        title={mapKey ? (addStep === 'details' ? 'Address details' : undefined) : 'Add address'}
        fullScreen={isMobile}
        bleed={mapKey && addStep === 'pin'}
        maxWidth={addStep === 'pin' ? 880 : 520}
        height={!isMobile && mapKey && addStep === 'pin' ? 'min(760px, 90vh)' : undefined}
      >
        {mapKey ? (
          <AddAddressFormMap
            onSave={handleAddSuccess}
            onCancel={closeAddModal}
            onStepChange={onAddStepChange}
            mobile={mobile}
          />
        ) : (
          <AddAddressFormManual onSave={handleAddSuccess} onCancel={closeAddModal} />
        )}
      </Modal>
    </Box>
  );
}
